import { useEffect, useRef, useState } from "react";

/**
 * Tracks whether `query` currently matches, and optionally calls `onChange`
 * whenever the match state flips.
 */
export function useMediaQuery(
  query: string,
  onChange?: (matches: boolean) => void,
): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  // Keep a ref to onChange to avoid re-running use-effect when the callback changes
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const list = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
      onChangeRef.current?.(event.matches);
    };
    list.addEventListener("change", handler);
    return () => {
      list.removeEventListener("change", handler);
    };
  }, [query]);

  return matches;
}
